import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Table, Badge, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import TableCard from '../../components/common/TableCard';
import ConfirmModal from '../../components/common/ConfirmModal';
import AddItemFolioModal from '../../components/Configs/AddItemFolioModal';
import EditItemFolioModal from '../../components/Configs/EditItemFolioModal';
import { getFolios, updateFolios } from '../../services/Configs/ItemFolios';

function ItemFolios() {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);

    const [showAddFolioModal, setShowAddFolioModal] = useState(false);
    const [showEditFolioModal, setShowEditFolioModal] = useState(false);
    const [selectedItemFolioId, setSelectedItemFolioId] = useState(0);

    // filtros
    const [statusFilter, setStatusFilter] = useState('active');
    const [search, setSearch] = useState('');
    const [pageSize, setPageSize] = useState(10);

    // paginación
    const [page, setPage] = useState(1);

    // confirm eliminar
    const [confirm, setConfirm] = useState({
        show: false,
        id: null,
        name: '',
        isLoading: false,
    });

    const fetchRows = async () => {
        setLoading(true);
        try {
            const onlyActives = statusFilter === 'active';
            const res = await getFolios(1, 5000, onlyActives);
            setRows(Array.isArray(res?.items) ? res.items : []);
        } catch (e) {
            console.error(e);
            toast.error('No se pudieron cargar los folios.');
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRows();
    }, [statusFilter]);

    useEffect(() => {
        setPage(1);
    }, [statusFilter, search, pageSize]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => String(r.name ?? '').toLowerCase().includes(q));
    }, [rows, search]);

    const pageCount = Math.ceil(filtered.length / pageSize);
    const safePageCount = pageCount; // puede ser 0
    const currentPage = safePageCount === 0 ? 0 : Math.min(page, safePageCount);

    const pagedRows = useMemo(() => {
        if (safePageCount === 0) return [];
        const start = (currentPage - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, currentPage, pageSize, safePageCount]);

    const totalRows = filtered.length;

    const getVisiblePages = (currentPage, totalPages, maxButtons = 5) => {
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + maxButtons - 1);

        if (end - start < maxButtons - 1) {
            start = Math.max(1, end - maxButtons + 1);
        }

        const pages = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    };

    const visiblePages = getVisiblePages(page, pageCount);

    const handleAddFolio = () => setShowAddFolioModal(true);

    const handleOpenEditModal = (id) => {
        setSelectedItemFolioId(id);
        setShowEditFolioModal(true);
    };

    const askDelete = (item) => setConfirm({ show: true, id: item.id, name: item.name, isLoading: false });

    const confirmDelete = async () => {
        if (!confirm.id) return;

        setConfirm((p) => ({ ...p, isLoading: true }));
        try {
            await updateFolios(confirm.id, confirm.name, false);
            toast.success('Folio eliminado.');
            setConfirm({ show: false, id: null, name: '', isLoading: false });
            fetchRows();
        } catch (e) {
            console.error(e);
            toast.error('No se pudo eliminar el folio.');
            setConfirm((p) => ({ ...p, isLoading: false }));
        }
    };

    return (
        <>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-3">
                <h2 className="mb-0">Folios</h2>
                <Button variant="primary" onClick={handleAddFolio}>
                    Agregar
                </Button>
            </div>

            {/* Filtros */}
            <Row className="g-3 align-items-end mb-3">
                <Col md={3}>
                    <Form.Label>Estado</Form.Label>
                    <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="active">Activos</option>
                        <option value="all">Todos</option>
                    </Form.Select>
                </Col>

                <Col md={6}>
                    <Form.Label>Buscar</Form.Label>
                    <Form.Control
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por folio..."
                    />
                </Col>

                <Col md={3}>
                    <Form.Label>Por página</Form.Label>
                    <Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                        {[10, 20, 30, 50].map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            {/* Tabla */}
            <TableCard>
                <Table bordered hover responsive className="mb-0 align-middle">
                    <thead>
                        <tr>
                            <th className="actions-col">Acciones</th>
                            <th>Folios</th>
                            <th>Estatus</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={3} className="text-center py-5">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Cargando...
                                </td>
                            </tr>
                        ) : pagedRows.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="text-center py-5">Sin registros</td>
                            </tr>
                        ) : (
                            pagedRows.map((item) => (
                                <tr key={item.id}>
                                    <td className="actions-col">
                                        <div className="actions-cell">
                                            <Button variant="outline-primary" size="sm" onClick={() => handleOpenEditModal(item.id)}>
                                                Editar
                                            </Button>
                                            <Button variant="outline-danger" size="sm" onClick={() => askDelete(item)}>
                                                Eliminar
                                            </Button>
                                        </div>
                                    </td>

                                    <td className="col-nowrap">{item.name}</td>

                                    <td>
                                        {item.isActive ? <Badge bg="success">Activa</Badge> : <Badge bg="danger">Inactiva</Badge>}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </TableCard>

            {/* Paginación */}
            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <div className="text-muted">
                    Página {currentPage} de {safePageCount} ({totalRows} registros)
                </div>

                <div className="d-flex gap-2 align-items-center flex-wrap">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPage(1)}
                        disabled={safePageCount === 0 || currentPage <= 1}
                    >
                        «
                    </Button>

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={safePageCount === 0 || currentPage <= 1}
                    >
                        Anterior
                    </Button>

                    {visiblePages.length > 0 && visiblePages[0] > 1 && (
                        <>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setPage(1)}
                            >
                                1
                            </Button>
                            {visiblePages[0] > 2 && <span className="px-1 text-muted">…</span>}
                        </>
                    )}

                    {visiblePages.map((pageNumber) => (
                        <Button
                            key={pageNumber}
                            size="sm"
                            variant={pageNumber === currentPage ? 'secondary' : 'outline-secondary'}
                            onClick={() => setPage(pageNumber)}
                        >
                            {pageNumber}
                        </Button>
                    ))}

                    {visiblePages.length > 0 && visiblePages[visiblePages.length - 1] < safePageCount && (
                        <>
                            {visiblePages[visiblePages.length - 1] < safePageCount - 1 && (
                                <span className="px-1 text-muted">…</span>
                            )}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setPage(safePageCount)}
                            >
                                {safePageCount}
                            </Button>
                        </>
                    )}

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(safePageCount, p + 1))}
                        disabled={safePageCount === 0 || currentPage >= safePageCount}
                    >
                        Siguiente
                    </Button>

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setPage(safePageCount)}
                        disabled={safePageCount === 0 || currentPage >= safePageCount}
                    >
                        »
                    </Button>
                </div>
            </div>

            {/* Modales */}
            <AddItemFolioModal
                show={showAddFolioModal}
                handleClose={() => setShowAddFolioModal(false)}
                handleReload={fetchRows}
            />

            <EditItemFolioModal
                FolioId={selectedItemFolioId}
                show={showEditFolioModal}
                handleClose={() => setShowEditFolioModal(false)}
                handleReload={fetchRows}
            />

            {/* Confirm eliminar */}
            <ConfirmModal
                show={confirm.show}
                onHide={() => setConfirm({ show: false, id: null, name: '', isLoading: false })}
                onConfirm={confirmDelete}
                title="Eliminar folio"
                message={`¿Seguro que deseas eliminar "${confirm.name}"?`}
                confirmText="Eliminar"
                confirmVariant="danger"
                denyText={null}
                isLoading={confirm.isLoading}
            />
        </>
    );
}

export default ItemFolios;